import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Bell, Check, Loader2, Mail, Youtube, Music2, Instagram } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a027839a-8849-4776-a8ba-c961dd10b69f/artifacts/co2pjy4b_IMG_20260323_062123.jpg";
const YOUTUBE_URL = "https://youtube.com/@letsgetintoitfbe?si=lyzA8U32o3csvxTK";
const SPOTIFY_URL = "https://open.spotify.com/show/6B1NRfylqvaSDTedOPD3iz?si=RGWDLBCTQgOY7Hoh1b-mCg";
const INSTAGRAM_URL = "https://www.instagram.com/letsgetintoitfbe";
const TIKTOK_URL = "https://www.tiktok.com/@letsgetintoitfbe";

const TikTokIcon = ({ size = 18, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.3z"/>
  </svg>
);

export default function SubscribePage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setMessage('');
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email, name });
      setSuccess(true);
      setMessage(data.message);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="subscribe-page" className="pt-16 md:pt-20 min-h-screen">
      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1547754440-19878c025ea5?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 to-[#0A0A0A]" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-20 h-20 bg-[#FF2A2A]/10 flex items-center justify-center mx-auto mb-6">
              <Bell size={36} className="text-[#FF2A2A]" />
            </div>
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-4">
              NEVER MISS AN EPISODE
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase leading-none mb-6">
              SUBSCRIBE
            </h1>
            <p className="font-body text-base md:text-lg text-[#A1A1AA] max-w-lg mx-auto">
              Get notified when new episodes drop and stay updated on events. Join the community!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Subscribe Form */}
      <section className="max-w-xl mx-auto px-4 md:px-8 -mt-8 relative z-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {success ? (
            <div className="bg-[#141414] border border-[#FF2A2A]/30 p-10 text-center" data-testid="subscribe-success">
              <div className="w-16 h-16 bg-[#FF2A2A]/10 flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[#FF2A2A]" />
              </div>
              <h2 className="font-heading text-3xl uppercase mb-3">YOU'RE IN!</h2>
              <p className="font-body text-sm text-[#A1A1AA]">{message}</p>
              <p className="font-body text-sm text-[#71717A] mt-4">
                Follow us on socials while you wait for the next episode!
              </p>
              <div className="flex justify-center gap-4 mt-6">
                <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#FF2A2A]/10 flex items-center justify-center hover:bg-[#FF2A2A]/20 transition-colors">
                  <Youtube size={18} className="text-[#FF2A2A]" />
                </a>
                <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#1DB954]/10 flex items-center justify-center hover:bg-[#1DB954]/20 transition-colors">
                  <Music2 size={18} className="text-[#1DB954]" />
                </a>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#E1306C]/10 flex items-center justify-center hover:bg-[#E1306C]/20 transition-colors">
                  <Instagram size={18} className="text-[#E1306C]" />
                </a>
                <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#00f2ea]/10 flex items-center justify-center hover:bg-[#00f2ea]/20 transition-colors">
                  <TikTokIcon size={18} className="text-[#00f2ea]" />
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#141414] border border-white/5 p-8 space-y-5" data-testid="subscribe-form">
              <div className="flex items-center gap-3 mb-2">
                <Mail size={20} className="text-[#FF2A2A]" />
                <h2 className="font-heading text-xl uppercase">GET UPDATES</h2>
              </div>

              {message && !success && (
                <div data-testid="subscribe-error" className="bg-[#FF2A2A]/10 border border-[#FF2A2A]/30 p-3 text-center">
                  <p className="font-body text-sm text-[#FF2A2A]">{message}</p>
                </div>
              )}

              <div className="space-y-2">
                <Input
                  data-testid="subscribe-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="bg-[#0A0A0A] border-white/10 text-white h-12 rounded-none focus:border-[#FF2A2A] focus:ring-1 focus:ring-[#FF2A2A] placeholder:text-[#71717A]"
                />
              </div>

              <div className="space-y-2">
                <Input
                  data-testid="subscribe-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="bg-[#0A0A0A] border-white/10 text-white h-12 rounded-none focus:border-[#FF2A2A] focus:ring-1 focus:ring-[#FF2A2A] placeholder:text-[#71717A]"
                />
              </div>

              <Button
                type="submit"
                data-testid="subscribe-submit-btn"
                disabled={loading || !email.trim()}
                className="w-full bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide h-12 rounded-none hover:bg-[#E60000] transition-all disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={18} className="animate-spin" /> Subscribing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Bell size={18} /> SUBSCRIBE FOR UPDATES
                  </span>
                )}
              </Button>

              <p className="font-body text-xs text-[#71717A] text-center">
                We'll only email you about new episodes and events. No spam.
              </p>
            </form>
          )}
        </motion.div>
      </section>

      {/* Also follow us */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center">
        <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-4">
          ALSO FOLLOW US ON
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sub-youtube-link"
            className="flex items-center gap-2 bg-[#141414] border border-white/5 hover:border-[#FF2A2A]/50 px-5 py-3 transition-all group"
          >
            <Youtube size={18} className="text-[#FF2A2A]" />
            <span className="font-body text-sm font-semibold">YouTube</span>
          </a>
          <a
            href={SPOTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sub-spotify-link"
            className="flex items-center gap-2 bg-[#141414] border border-white/5 hover:border-[#1DB954]/50 px-5 py-3 transition-all group"
          >
            <Music2 size={18} className="text-[#1DB954]" />
            <span className="font-body text-sm font-semibold">Spotify</span>
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sub-instagram-link"
            className="flex items-center gap-2 bg-[#141414] border border-white/5 hover:border-[#E1306C]/50 px-5 py-3 transition-all group"
          >
            <Instagram size={18} className="text-[#E1306C]" />
            <span className="font-body text-sm font-semibold">Instagram</span>
          </a>
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sub-tiktok-link"
            className="flex items-center gap-2 bg-[#141414] border border-white/5 hover:border-[#00f2ea]/50 px-5 py-3 transition-all group"
          >
            <TikTokIcon size={18} className="text-[#00f2ea]" />
            <span className="font-body text-sm font-semibold">TikTok</span>
          </a>
        </div>
      </section>
    </main>
  );
}
